import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import { Box, Typography } from "@mui/material";
import {
  Player,
  renderPlayerOverlay,
  STATIC_PLAYER_OVERLAY_STATE,
  type PlayerOverlay,
  type PlayerProps,
} from "@/shared/components/Player";
import CreateViewMutation from "@/shared/graphql/mutations/CreateViewMutation.graphql";
import ContentAccessQuery from "@/shared/graphql/queries/ContentAccessQuery.graphql";
import { lastTouchInput } from "@/shared/utils/attribution";
import { getViewerId } from "@/shared/utils/viewer";
import { useUser } from "@/shared/hooks/use-user/useUser";
import { useTranslation } from "@/shared/hooks";
import { AccessErrorScreen } from "./AccessErrorScreen";
import { PaymentScreen, EmbedPaymentScreen } from "./PaymentScreen";
import { ParallelLimitScreen } from "./ParallelLimitScreen";
import { useViewTracking } from "./useViewTracking";
import { isParallelLimitError } from "./viewSessions";
import {
  ContentAccessErrorTypeType,
  type ContentAccessQueryType,
  type ContentQueryType,
  type CreateViewMutationType,
} from "@/types/graphql/site.generated";

export interface ContentPlayerProps {
  content: ContentQueryType["content"];
  source: "SITE" | "EMBED";
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  start?: number | null;
  fill?: boolean;
  overlay?: PlayerOverlay;
  pauseOverlay?: ReactNode;
  /** Сквозной проброс в Player.nextButton (кнопка «Далее» автоперехода). */
  nextButton?: PlayerProps["nextButton"];
  /** Сквозной проброс меток таймлайна (маркеры + аннотации). */
  timecodes?: PlayerProps["timecodes"];
  /** Сквозной проброс в Player.isLive (LIVE/rewound-бейдж вместо времени). */
  isLive?: boolean;
  /** Клик по цене на экране оплаты (PaymentScreen): открыть покупку. */
  onProductClick?: (
    products: ContentQueryType["content"]["products"]["nodes"],
  ) => void;
  /** Кнопка «Разобраться с помощником» на экранах ошибок (Task 15).
   * ContentPlayer живёт в shared и не может импортировать features/assistant
   * (FSD-границы), вызывающая сторона (widgets, которым features доступны)
   * передаёт готовый рендер, ContentPlayer сам решает, где его показать, и
   * никогда не вызывает на embed-страницах (там нет виджета помощника). */
  renderAssistantAction?: (context: {
    errorCode: ContentAccessErrorTypeType | "PLAYBACK_ERROR";
    contentId: string;
    /** Для PLAYBACK_ERROR: тип и детали ошибки плеера, HTTP-код. */
    playerError?: string | null;
  }) => ReactNode;
}

const FINISHED_PERCENT = 95;

export const ContentPlayer: FC<ContentPlayerProps> = ({
  content,
  source,
  autoPlay = true,
  muted = false,
  loop = false,
  controls = true,
  start,
  fill = false,
  overlay,
  pauseOverlay,
  nextButton,
  timecodes,
  isLive,
  onProductClick,
  renderAssistantAction,
}) => {
  const { user } = useUser();
  const { t } = useTranslation();
  const [view, setView] = useState<CreateViewMutationType["createView"] | null>(
    null,
  );
  const [failed, setFailed] = useState(false);
  // Poster → (Play click or autoPlay) → createView, per spec: a session/view
  // must not be created just because the page mounted with autoPlay=false —
  // only once the caller actually asked to autoplay, or the viewer clicked.
  const [intentToPlay, setIntentToPlay] = useState(autoPlay);

  // Лимит параллельных просмотров платного контента: createView или
  // сердцебиение ответили, что место занято другим устройством.
  const [limitReached, setLimitReached] = useState(false);
  // Повторный запрос доступа после «Смотреть здесь» не удался: ошибка на
  // экране лимита (иначе зритель видел бы прежний экран без объяснений).
  const [takeOverStartFailed, setTakeOverStartFailed] = useState(false);
  // Доступ, перезапрошенный после «Смотреть здесь» (с viewerId), поверх
  // доступа из запроса страницы.
  const [accessOverride, setAccessOverride] = useState<
    ContentQueryType["content"]["siteAccess"] | null
  >(null);

  const access =
    accessOverride ??
    (source === "EMBED" ? content.embedAccess : content.siteAccess);

  const [createView] = useMutation<CreateViewMutationType>(CreateViewMutation);
  const apolloClient = useApolloClient();

  // Смена контента на том же инстансе (автопереход к следующему видео, клик
  // по «Похожим») НЕ должна размонтировать плеер — фулскрин живёт на его
  // контейнере и слетает при remount. Поэтому вместо key={content.id} у
  // call sites — сброс view-состояния прямо в рендере при смене id
  // (React-паттерн «adjusting state when a prop changes»).
  const [viewContentId, setViewContentId] = useState(content.id);
  if (viewContentId !== content.id) {
    setViewContentId(content.id);
    setView(null);
    setFailed(false);
    setIntentToPlay(autoPlay);
    setLimitReached(false);
    setTakeOverStartFailed(false);
    setAccessOverride(null);
  }

  // Один createView на контент (guard по id, а не по `called` — оно не
  // сбрасывается при смене контента). Ответ применяется только если контент
  // к этому моменту не сменился: view запроса для прошлого видео, долетевший
  // после переключения, иначе подсунул бы плееру чужой плейлист.
  const requestedContentIdRef = useRef<string | null>(null);
  // Текущий контент для асинхронных ответов (повторный запрос доступа после
  // «Смотреть здесь» не должен применяться к сменившемуся видео).
  const currentContentIdRef = useRef(content.id);
  useEffect(() => {
    currentContentIdRef.current = content.id;
  }, [content.id]);
  useEffect(() => {
    if (requestedContentIdRef.current === content.id) return;
    if (access.token && !access.error && intentToPlay) {
      const requestedContentId = content.id;
      requestedContentIdRef.current = requestedContentId;
      const requestView = async () => {
        try {
          const result = await createView({
            variables: {
              token: access.token,
              viewerId: getViewerId(),
              utm: lastTouchInput(),
            },
          });
          if (requestedContentIdRef.current !== requestedContentId) return;
          if (result.data?.createView) setView(result.data.createView);
          else setFailed(true);
        } catch (error) {
          if (requestedContentIdRef.current !== requestedContentId) return;
          // Место заняли между выдачей токена и стартом (или токен выдан
          // без viewerId при SSR): экран лимита, а не «не удалось начать».
          if (isParallelLimitError(error)) setLimitReached(true);
          else setFailed(true);
        }
      };
      void requestView();
    }
  }, [content.id, access.token, access.error, createView, intentToPlay]);

  const startPosition = useMemo(() => {
    if (start != null) return start;
    const position = view?.video?.viewerVideoPosition;
    if (position && position.percent < FINISHED_PERCENT)
      return position.currentTime;
    return null;
  }, [start, view]);

  // Другое устройство забрало просмотр: плеер размонтируется (воспроизведение
  // останавливается), вместо него экран лимита.
  const handleViewSessionClosed = useCallback(() => {
    setLimitReached(true);
    setView(null);
  }, []);

  const tracking = useViewTracking({
    viewToken: view?.token,
    contentId: content.id,
    isAuthorized: Boolean(user),
    onViewSessionClosed: handleViewSessionClosed,
  });

  // «Смотреть здесь» удалось: заново спрашиваем доступ уже с viewerId этого
  // браузера и запускаем createView с новым токеном.
  const handleTakenOver = async () => {
    const requestedContentId = content.id;
    setTakeOverStartFailed(false);
    try {
      const { data } = await apolloClient.query<ContentAccessQueryType>({
        query: ContentAccessQuery,
        variables: {
          id: requestedContentId,
          source,
          viewerId: getViewerId(),
        },
        fetchPolicy: "network-only",
      });
      if (currentContentIdRef.current !== requestedContentId) return;
      const nextAccess = data?.content?.access;
      if (!nextAccess) {
        setTakeOverStartFailed(true);
        return;
      }
      requestedContentIdRef.current = null;
      setView(null);
      setFailed(false);
      setLimitReached(false);
      setAccessOverride(nextAccess);
      setIntentToPlay(true);
    } catch {
      if (currentContentIdRef.current !== requestedContentId) return;
      setTakeOverStartFailed(true);
    }
  };

  const showParallelLimit =
    limitReached ||
    access.error ===
      ContentAccessErrorTypeType.ContentIsNotAvailableByParallelLimitType;

  const poster = content.preview?.url ? String(content.preview.url) : null;

  // Экраны ниже (ошибка/недоступно/не удалось начать) не монтируют реальный
  // Player: оверлей получает статичное состояние (контролы видимы, каста
  // нет), что сохраняет прежнее поведение CloseRouteDialogButton и
  // PromoButtonWidget на этих экранах.
  const staticOverlay = renderPlayerOverlay(
    overlay,
    STATIC_PLAYER_OVERLAY_STATE,
  );

  // Кнопка помощника только на витрине (source==="SITE"): на embed-страницах
  // виджета помощника нет (AssistantWidget не смонтирован там, см. _app.tsx),
  // клик по кнопке не открыл бы ничего.
  const assistantActionFor = (
    errorCode: ContentAccessErrorTypeType | "PLAYBACK_ERROR",
    playerError?: string | null,
  ): ReactNode =>
    source === "SITE"
      ? renderAssistantAction?.({
          errorCode,
          contentId: content.id,
          playerError,
        })
      : undefined;

  // На экранах ошибок кнопка закрытия route-диалога тоже должна быть
  // доступна (в референсе closeRouteDialogButton прокинута и в
  // ContentLoading/офлайн-экраны) — поэтому оверлей рендерится и здесь.
  const renderScreen = (): ReactNode => {
    if (showParallelLimit) {
      return (
        <Box sx={{ position: "relative" }}>
          <ParallelLimitScreen
            source={source}
            contentId={content.id}
            contentUrl={content.url ? String(content.url) : null}
            poster={poster}
            onTakenOver={() => void handleTakenOver()}
            startFailed={takeOverStartFailed}
            assistantAction={assistantActionFor(
              ContentAccessErrorTypeType.ContentIsNotAvailableByParallelLimitType,
            )}
          />
          {staticOverlay}
        </Box>
      );
    }

    if (access.error) {
      if (
        access.error ===
        ContentAccessErrorTypeType.ContentIsNotAvailableForFreeViewingType
      ) {
        return (
          <Box sx={{ position: "relative" }}>
            {source === "EMBED" ? (
              <EmbedPaymentScreen poster={poster} />
            ) : (
              <PaymentScreen
                poster={poster}
                contentType={content.type}
                accessStartAt={content.accessStartAt}
                stream={content.stream}
                products={content.products.nodes}
                onProductClick={onProductClick}
                assistantAction={assistantActionFor(
                  ContentAccessErrorTypeType.ContentIsNotAvailableForFreeViewingType,
                )}
              />
            )}
            {staticOverlay}
          </Box>
        );
      }

      return (
        <Box sx={{ position: "relative" }}>
          <AccessErrorScreen
            error={access.error}
            accessStartAt={content.accessStartAt}
            poster={poster}
            assistantAction={assistantActionFor(access.error)}
          />
          {staticOverlay}
        </Box>
      );
    }

    if (view && !view.video) {
      return (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "16 / 9",
            borderRadius: 2,
            backgroundColor: "#000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ color: "#fff" }}>
            {t("player.unavailable")}
          </Typography>
          {staticOverlay}
        </Box>
      );
    }

    if (failed) {
      return (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "16 / 9",
            borderRadius: 2,
            backgroundColor: "#000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ color: "#fff" }}>
            {t("player.startFailed")}
          </Typography>
          {staticOverlay}
        </Box>
      );
    }

    return (
      <Player
        src={view?.video?.playlist ? String(view.video.playlist) : null}
        poster={poster}
        title={content.title}
        autoPlay={intentToPlay}
        muted={muted}
        loop={loop}
        controls={controls}
        startPosition={startPosition}
        cmcdContentId={content.id}
        fill={fill}
        overlay={overlay}
        pauseOverlay={pauseOverlay}
        nextButton={nextButton}
        timecodes={timecodes}
        isLive={isLive}
        onTimeUpdate={tracking.onTimeUpdate}
        onPause={tracking.onPauseOrEnded}
        onEnded={tracking.onPauseOrEnded}
        getVideoElement={tracking.setVideoElement}
        onPlayIntent={() => setIntentToPlay(true)}
        renderPlaybackErrorAction={(errorDetails) =>
          assistantActionFor("PLAYBACK_ERROR", errorDetails)
        }
      />
    );
  };

  // className="paywall" у платного контента: на него ссылается JSON-LD
  // (VideoObject.hasPart WebPageElement.cssSelector, см.
  // StructuredData/builders.ts), Google так отличает пейволл от клоакинга.
  // Класс стоит на обёртке любого экрана (ожидание стрима, оплата, плеер),
  // а не только на экране оплаты: разметка должна находить элемент и в
  // SSR-HTML, где доступ ещё не проверен. Обёртка рендерится всегда с
  // display: contents, чтобы дерево не менялось при смене контента (Player
  // не должен размонтироваться, см. комментарий у viewContentId).
  // Не переименовывать без правки разметки.
  return (
    <Box
      className={content.premium ? "paywall" : undefined}
      sx={{ display: "contents" }}
    >
      {renderScreen()}
    </Box>
  );
};
